import { useLazyQuery, useMutation, useQuery } from "@apollo/client";
import {
  DISCOVER_OUR_SERVICES,
  FREE_DESIGN_FORM,
  INSTALLATION_FORM,
  TECHNICAL_ASSISTANCE_FORM,
  UPLOAD_INVOICE,
} from "./discoverOurServices.gql";
import { useState } from "react";
import Modal from "react-bootstrap/Modal";
import styles from "./DiscoverServicesForm.module.css";
import { Field, Form, Formik } from "formik";
import * as Yup from "yup";
import { useToasts } from "react-toast-notifications";
import { useSelector } from "react-redux";
import { CityInput } from "./CityInput";
import ReCAPTCHA from "react-google-recaptcha";

const MAX_SIZE_MB = 5;
const MAX_SIZE_BYTES = MAX_SIZE_MB * 1024 * 1024; // Convert MB to bytes

const fileToBase64 = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const base64String = reader.result.split(",")[1];
      resolve(base64String);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
};

const installationSchema = Yup.object().shape({
  fullname: Yup.string()
    .matches(/^[A-Za-z]+(?:[-\s][A-Za-z]+)*$/, "Only alphabets allowed")
    .max(50, "Name exceeds the maximum character limit of 50")
    .required("Name is required"),
  email: Yup.string()
    .email("Invalid email")
    .matches(
      /^[\w-]+(\.[\w-]+)*@[\w-]+(\.[\w-]{2,10})+$/,
      "Invalid email format"
    )
    .required("Email is required"),
  phone: Yup.string()
    .matches(/^[6-9]\d{9}$/, "Phone number is not valid")
    .required("Mobile is required"),
  sourceOfPurchase: Yup.string().required("Select source of purchase"),
  productPurchased: Yup.string(),
  type: Yup.string().required("form type required"),
  invoice: Yup.mixed()
    .test("fileType", "Only PDF files are allowed", (value) => {
      if (value) {
        return value.type === "application/pdf";
      } else {
        return true;
      }
    })
    .test(
      "fileSize",
      `File size must be less than ${MAX_SIZE_MB}MB`,
      (value) => {
        if (!value) return true;
        return value.size <= MAX_SIZE_BYTES;
      }
    )
    .nullable()
    .required("Please upload the invoice"),
  city: Yup.string().required("Enter city name"),
  pincode: Yup.string().required("pincode is required"),
  // message: Yup.string().required("Enter comment"),
  terms: Yup.boolean()
    .oneOf([true], "Please agree to the terms and conditions")
    .required("Please agree to the terms and conditions"),
  captcha: Yup.string().required("This is required"),
  houseNo: Yup.string().required("Enter house no."),
  area: Yup.string().required("Enter area name"),
  landmark: Yup.string(),
});

const freeDesignSchema = Yup.object().shape({
  fullname: Yup.string()
    .matches(/^[A-Za-z]+(?:[-\s][A-Za-z]+)*$/, "Only alphabets allowed")
    .max(50, "Name exceeds the maximum character limit of 50")
    .required("Name is required"),
  email: Yup.string()
    .email("Invalid email")
    .matches(
      /^[\w-]+(\.[\w-]+)*@[\w-]+(\.[\w-]{2,10})+$/,
      "Invalid email format"
    )
    .required("Email is required"),
  phone: Yup.string()
    .matches(/^[6-9]\d{9}$/, "Phone number is not valid")
    .required("Mobile is required"),
  // message: Yup.string().required("Enter comment"),
  city: Yup.string().required("Enter city name"),
  pincode: Yup.string().required("pincode is required"),
  formType: Yup.string(),
  terms: Yup.boolean()
    .oneOf([true], "Please agree to the terms and conditions")
    .required("Please agree to the terms and conditions"),
  captcha: Yup.string().required("This is required"),
  houseNo: Yup.string().required("Enter house no."),
  area: Yup.string().required("Enter area name"),
  landmark: Yup.string(),
});

const technicalAssistanceSchema = Yup.object().shape({
  fullname: Yup.string()
    .matches(/^[A-Za-z]+(?:[-\s][A-Za-z]+)*$/, "Only alphabets allowed")
    .max(50, "Name exceeds the maximum character limit of 50")
    .required("Name is required"),
  email: Yup.string()
    .email("Invalid email")
    .matches(
      /^[\w-]+(\.[\w-]+)*@[\w-]+(\.[\w-]{2,10})+$/,
      "Invalid email format"
    )
    .required("Email is required"),
  phone: Yup.string()
    .matches(/^[6-9]\d{9}$/, "Phone number is not valid")
    .required("Mobile is required"),
  productCategory: Yup.string().required("Select product category"),
  productName: Yup.string(),
  // message: Yup.string().required("Enter comment"),
  city: Yup.string().required("Enter city name"),
  pincode: Yup.string().required("pincode is required"),
  formType: Yup.string(),
  terms: Yup.boolean()
    .oneOf([true], "Please agree to the terms and conditions")
    .required("Please agree to the terms and conditions"),
  captcha: Yup.string().required("This is required"),
  houseNo: Yup.string().required("Enter house no."),
  area: Yup.string().required("Enter area name"),
  landmark: Yup.string(),
});

export const useDiscoverOurServices = () => {
  const {
    data,
    loading,
    error: discoveryError,
  } = useQuery(DISCOVER_OUR_SERVICES, {
    context: { fetchOptions: { method: "GET" } },
  });
  const [showInstallationModal, setShowInstallationModal] = useState(false);
  const [showTechnicalAssistanceModal, setShowTechnicalAssistanceModal] =
    useState(false);
  const [showFreeDesignModal, setShowFreeDesignModal] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const { addToast } = useToasts();
  const [selectedCityOption, setSelectedCityOption] = useState(null);
  let resetFunc;

  const [
    freeDesignForm,
    {
      data: freeDesignFormData,
      loading: freeDesignFormLoading,
      error: freeDesignFormError,
    },
  ] = useMutation(FREE_DESIGN_FORM);

  const [
    technicalForm,
    {
      data: technicalFormData,
      loading: technicalFormLoading,
      error: technicalFormError,
    },
  ] = useMutation(TECHNICAL_ASSISTANCE_FORM);

  const [
    installationForm,
    {
      data: installationFormData,
      loading: installationFormLoading,
      error: installationFormError,
    },
  ] = useMutation(INSTALLATION_FORM);

  const [
    uploadContactInvoice,
    {
      data: uploadInvoiceData,
      loading: uploadInvoiceLoading,
      error: uploadInvoiceError,
    },
  ] = useMutation(UPLOAD_INVOICE);

  function onChange(setFieldValue, e) {
    if (e !== null) {
      setFieldValue("captcha", e);
    } else {
      setFieldValue("captcha", "");
    }
  }

  const { ConfigData } = useSelector((state) => state.homePageSlice);

  const technicalAssistanceModal = (
    <Modal
      show={showTechnicalAssistanceModal}
      onHide={() => setShowTechnicalAssistanceModal(false)}
      className={styles.technicalModal}
      centered
    >
      <Modal.Header className={styles.modalHeader}>
        <Modal.Title className={styles.technicalModalheading}>
          Technical Assistance
        </Modal.Title>
        <div className={styles["close-btn"]}>
          <button
            type="button"
            data-bs-dismiss="modal"
            aria-label="Close"
            onClick={() => {
              resetFunc();
              setShowTechnicalAssistanceModal(false);
              setSelectedCityOption(null);
              setInputValue("");
            }}
            className={styles.technicalModalbutton}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="1em"
              height="1em"
              viewBox="0 0 24 24"
            >
              <path
                fill="white"
                d="M18.3 5.71a.996.996 0 0 0-1.41 0L12 10.59L7.11 5.7A.996.996 0 1 0 5.7 7.11L10.59 12L5.7 16.89a.996.996 0 1 0 1.41 1.41L12 13.41l4.89 4.89a.996.996 0 1 0 1.41-1.41L13.41 12l4.89-4.89c.38-.38.38-1.02 0-1.4"
              />
            </svg>
          </button>
        </div>
      </Modal.Header>
      <Modal.Body className={styles.modalBody}>
        <div>
          <p className={styles.sampleText}>
            Are you looking for some technical assistance before making a
            purchase? We got you!
          </p>
          <p className={styles.sampleText}>
            You can call us on our toll-free number at{" "}
            <span>{ConfigData?.support_phone}</span>, or write to us at
            <span> {ConfigData?.support_email}</span>.If you prefer a call-back,
            please fill out the form below, and we will reach out to you:
          </p>
        </div>
        <Formik
          initialValues={{
            fullname: "",
            email: "",
            message: "",
            formType: "Technical Assistance",
            phone: "",
            productCategory: "",
            productName: "",
            city: "",
            pincode: "",
            terms: false,
            captcha: "",
            houseNo: "",
            area: "",
            landmark: "",
          }}
          validationSchema={technicalAssistanceSchema}
          onSubmit={async (values, { resetForm }) => {
            try {
              const response = await technicalForm({
                variables: {
                  fullname: values.fullname,
                  type: values.formType,
                  email: values.email,
                  phone: values.phone,
                  message: values.message,
                  productCategory: values.productCategory,
                  productName: values.productName,
                  city: values.city,
                  pincode: values.pincode,
                  g_captcha_response: values.captcha,
                  houseNo: values.houseNo,
                  areaName: values.area,
                  landmark: values.landmark,
                },
              });

              if (response?.data?.contactusFormSubmit?.success_message) {
                resetForm();
                setShowTechnicalAssistanceModal(false);
                addToast(response?.data?.contactusFormSubmit?.success_message, {
                  appearance: "success",
                  autoDismiss: true,
                  style: {
                    backgroundColor: "#027B51" /* Custom background color */,
                    color: "#fff" /* Custom text color */,
                  },
                });
                setSelectedCityOption(null);
                setInputValue("");
              }
            } catch (error) {
              addToast("Something went wrong", {
                appearance: "error",
                autoDismiss: true,
                style: {
                  backgroundColor: "#DB1F00",
                  color: "#fff",
                },
              });
            }
          }}
        >
          {({ errors, touched, resetForm, setFieldValue }) => {
            resetFunc = resetForm;
            return (
              <Form>
                <div className="row g-3">
                  <div className="col-md-6">
                    <label htmlFor={"fullname"} className="form-label">
                      Name <span className={styles.mandatory}> *</span>
                    </label>
                    <div className={styles.inputbx}>
                      <Field
                        placeholder="Enter Name"
                        id="fullname"
                        name="fullname"
                        className="form-control"
                      />
                    </div>
                    {errors.fullname && touched.fullname ? (
                      <p style={{ color: "#FF0000", fontSize: "12px" }}>
                        {errors.fullname}
                      </p>
                    ) : null}
                  </div>

                  <div className="col-md-6">
                    <label htmlFor={"email"} className="form-label">
                      Email <span className={styles.mandatory}> *</span>
                    </label>
                    <div className={styles.inputbx}>
                      <Field
                        placeholder="Enter Email"
                        id="email"
                        name="email"
                        className="form-control"
                      />
                    </div>
                    {errors.email && touched.email ? (
                      <p style={{ color: "#FF0000", fontSize: "12px" }}>
                        {errors.email}
                      </p>
                    ) : null}
                  </div>
                  <div className="col-md-12">
                    <label htmlFor={"phone"} className="form-label">
                      Contact Number{" "}
                      <span className={styles.mandatory}> *</span>
                    </label>
                    <div className={styles.inputbx}>
                      <Field
                        type="text"
                        name="phone"
                        id="phone"
                        className="form-control"
                        placeholder="Enter Contact Number"
                      />
                    </div>
                    {errors.phone && touched.phone ? (
                      <p style={{ color: "#FF0000", fontSize: "12px" }}>
                        {errors.phone}
                      </p>
                    ) : null}
                  </div>
                  <div className="col-md-12">
                    <label htmlFor={"productCategory"} className="form-label">
                      Product Category{" "}
                      <span className={styles.mandatory}> *</span>
                    </label>
                    <div className={styles.inputbx}>
                      <Field
                        as="select"
                        name="productCategory"
                        className={`form-select ${styles.select}`}
                        disabled={!ConfigData?.product_category}
                      >
                        {ConfigData ? (
                          <>
                            <option value="">Select Product Category</option>
                            {ConfigData?.product_category
                              .split("\r\n")
                              .map((item, idx) => (
                                <option value={item} label={item} key={idx} />
                              ))}
                          </>
                        ) : null}
                      </Field>
                    </div>
                    {errors.productCategory && touched.productCategory ? (
                      <p style={{ color: "#FF0000", fontSize: "12px" }}>
                        {errors.productCategory}
                      </p>
                    ) : null}
                  </div>

                  <div className="col-md-12">
                    <label htmlFor={"productName"} className="form-label">
                      Product Name
                    </label>
                    <div className={styles.inputbx}>
                      <Field
                        type="text"
                        name="productName"
                        id="productName"
                        className="form-control"
                        placeholder="Enter Product Name"
                      />
                    </div>
                    {errors.productName && touched.productName ? (
                      <p style={{ color: "#FF0000", fontSize: "12px" }}>
                        {errors.productName}
                      </p>
                    ) : null}
                  </div>

                  <div className="col-md-6">
                    <label htmlFor="houseNo">
                      House / Flat No.{" "}
                      <span className={styles.mandatory}> *</span>
                    </label>
                    <div className={styles.inputbx}>
                      <Field
                        type="text"
                        name="houseNo"
                        id="houseNo"
                        className="form-control"
                        placeholder="Enter House Number"
                      />
                    </div>
                    {errors.houseNo && touched.houseNo ? (
                      <p style={{ color: "#FF0000", fontSize: "12px" }}>
                        {errors.houseNo}
                      </p>
                    ) : null}
                  </div>

                  <div className="col-md-6">
                    <label htmlFor="area">
                      Area / Building Name{" "}
                      <span className={styles.mandatory}> *</span>
                    </label>
                    <div className={styles.inputbx}>
                      <Field
                        type="text"
                        name="area"
                        id="area"
                        className="form-control"
                        placeholder="Enter Area Name"
                      />
                    </div>
                    {errors.area && touched.area ? (
                      <p style={{ color: "#FF0000", fontSize: "12px" }}>
                        {errors.area}
                      </p>
                    ) : null}
                  </div>

                  <div className="col-md-12">
                    <label htmlFor="landmark">Landmark</label>
                    <div className={styles.inputbx}>
                      <Field
                        type="text"
                        name="landmark"
                        id="landmark"
                        className="form-control"
                        placeholder="Enter Landmark"
                      />
                    </div>
                  </div>

                  <div className="col-md-12">
                    <label htmlFor="city" className="form-label">
                      City <span className={styles.mandatory}> *</span>
                    </label>
                    <div className={styles.searchWrapper}>
                      <CityInput
                        setSelectedCityOption={setSelectedCityOption}
                        setInputValue={setInputValue}
                        selectedCityOption={selectedCityOption}
                        inputValue={inputValue}
                      />
                    </div>
                    {errors.city && touched.city ? (
                      <p style={{ color: "#FF0000", fontSize: "12px" }}>
                        {errors.city}
                      </p>
                    ) : null}
                  </div>

                  <div className="col-12">
                    <label htmlFor="message" className="form-label">
                      Any Comments
                      {/* <span className={styles.mandatory}> *</span> */}
                    </label>
                    <div className={styles.inputbx}>
                      <Field
                        as="textarea"
                        rows="2"
                        className={`${styles.textArea} form-control`}
                        id="message"
                        name="message"
                      />
                    </div>
                    {errors.message && touched.message ? (
                      <p style={{ color: "#FF0000", fontSize: "12px" }}>
                        {errors.message}
                      </p>
                    ) : null}
                  </div>
                  {ConfigData?.recaptcha_site_key && (
                    <div className="col-12">
                      <ReCAPTCHA
                        sitekey={ConfigData?.recaptcha_site_key}
                        onChange={(e) => onChange(setFieldValue, e)}
                      />
                      {errors.captcha && touched.captcha ? (
                        <p style={{ color: "#FF0000", fontSize: "12px" }}>
                          {errors.captcha}
                        </p>
                      ) : null}
                    </div>
                  )}
                  <div className="col-12">
                    <div className={styles.checkWrapper}>
                      <Field type="checkbox" name="terms" />
                      <label htmlFor="terms" className="form-label">
                        I hereby agree and authorize Hettich India Pvt. Ltd and
                        all its subsidiaries to call / email / SMS / WhatsApp me
                        with product / service/ offer /other communications
                      </label>
                    </div>
                    {errors.terms && touched.terms ? (
                      <p style={{ color: "#FF0000", fontSize: "12px" }}>
                        {errors.terms}
                      </p>
                    ) : null}
                  </div>

                  <div className="col-12">
                    {!technicalFormLoading &&
                      technicalFormError?.message(
                        <p
                          style={{
                            textAlign: "center",
                            color: "#00FF00",
                            fontSize: "12px",
                          }}
                        >
                          {technicalFormError?.message}
                        </p>
                      )}
                    <div className="row">
                      <div className="col-md-12 text-center">
                        <button
                          type="submit"
                          className={`btn ${styles["btn-subit"]}`}
                          disabled={technicalFormLoading}
                        >
                          {technicalFormLoading ? (
                            <span
                              class="spinner-border spinner-border-sm text-light"
                              role="status"
                            >
                              <span class="visually-hidden">Loading...</span>
                            </span>
                          ) : (
                            "SUBMIT"
                          )}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </Form>
            );
          }}
        </Formik>
      </Modal.Body>
    </Modal>
  );

  const freeDesignModal = (
    <Modal
      show={showFreeDesignModal}
      onHide={() => setShowInstallationModal(false)}
      className={styles.freeDesignModal}
      centered
    >
      <Modal.Header className={styles.modalHeader}>
        <Modal.Title> Free Design</Modal.Title>
        <div className={styles["close-btn"]}>
          <button
            type="button"
            data-bs-dismiss="modal"
            aria-label="Close"
            onClick={() => {
              resetFunc();
              setShowFreeDesignModal(false);
              setSelectedCityOption(null);
              setInputValue("");
            }}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="1em"
              height="1em"
              viewBox="0 0 24 24"
            >
              <path
                fill="white"
                d="M18.3 5.71a.996.996 0 0 0-1.41 0L12 10.59L7.11 5.7A.996.996 0 1 0 5.7 7.11L10.59 12L5.7 16.89a.996.996 0 1 0 1.41 1.41L12 13.41l4.89 4.89a.996.996 0 1 0 1.41-1.41L13.41 12l4.89-4.89c.38-.38.38-1.02 0-1.4"
              />
            </svg>
          </button>
        </div>
      </Modal.Header>
      <Modal.Body className={styles.modalBody}>
        <p className={styles.sampleText}>
          Are you interested in our FREE Design Service?
        </p>
        <p className={styles.sampleText}>
          Please fill out the form below, and we will reach out to you:
        </p>

        <Formik
          initialValues={{
            fullname: "",
            formType: "Free Design Services",
            email: "",
            phone: "",
            message: "",
            city: "",
            pincode: "",
            terms: false,
            captcha: "",
            houseNo: "",
            area: "",
            landmark: "",
          }}
          validationSchema={freeDesignSchema}
          onSubmit={async (values, { resetForm }) => {
            try {
              const response = await freeDesignForm({
                variables: {
                  fullname: values.fullname,
                  type: values.formType,
                  email: values.email,
                  phone: values.phone,
                  message: values.message,
                  city: values.city,
                  pincode: values.pincode,
                  g_captcha_response: values.captcha,
                  houseNo: values.houseNo,
                  areaName: values.area,
                  landmark: values.landmark,
                },
              });

              if (response?.data?.contactusFormSubmit?.success_message) {
                resetForm();
                setShowFreeDesignModal(false);
                addToast(response?.data?.contactusFormSubmit?.success_message, {
                  appearance: "success",
                  autoDismiss: true,
                  style: {
                    backgroundColor: "#027B51" /* Custom background color */,
                    color: "#fff" /* Custom text color */,
                  },
                });
                setSelectedCityOption(null);
                setInputValue("");
              }
            } catch (error) {
              addToast("Something went wrong", {
                appearance: "error",
                autoDismiss: true,
                style: {
                  backgroundColor: "#DB1F00",
                  color: "#fff",
                },
              });
            }
          }}
        >
          {({ errors, touched, resetForm, setFieldValue }) => {
            resetFunc = resetForm;
            return (
              <Form>
                <div className="row g-3">
                  <div className="col-md-6">
                    <label htmlFor={"fullname"} className="form-label">
                      Name <span className={styles.mandatory}> *</span>
                    </label>
                    <div className={styles.inputbx}>
                      <Field
                        placeholder="Enter Name"
                        id="fullname"
                        name="fullname"
                        className="form-control"
                      />
                    </div>
                    {errors.fullname && touched.fullname ? (
                      <p style={{ color: "#FF0000", fontSize: "12px" }}>
                        {errors.fullname}
                      </p>
                    ) : null}
                  </div>

                  <div className="col-md-6">
                    <label htmlFor={"email"} className="form-label">
                      Email <span className={styles.mandatory}> *</span>
                    </label>
                    <div className={styles.inputbx}>
                      <Field
                        placeholder="Enter Email"
                        id="email"
                        name="email"
                        className="form-control"
                      />
                    </div>
                    {errors.email && touched.email ? (
                      <p style={{ color: "#FF0000", fontSize: "12px" }}>
                        {errors.email}
                      </p>
                    ) : null}
                  </div>
                  <div className="col-md-12">
                    <label htmlFor={"email"} className="form-label">
                      Contact Number{" "}
                      <span className={styles.mandatory}> *</span>
                    </label>
                    <div className={styles.inputbx}>
                      <Field
                        type="text"
                        name="phone"
                        id="phone"
                        className="form-control"
                        placeholder="Enter Contact Number"
                      />
                    </div>
                    {errors.phone && touched.phone ? (
                      <p style={{ color: "#FF0000", fontSize: "12px" }}>
                        {errors.phone}
                      </p>
                    ) : null}
                  </div>

                  <div className="col-md-6">
                    <label htmlFor="houseNo">
                      House / Flat No.{" "}
                      <span className={styles.mandatory}> *</span>
                    </label>
                    <div className={styles.inputbx}>
                      <Field
                        type="text"
                        name="houseNo"
                        id="houseNo"
                        className="form-control"
                        placeholder="Enter House Number"
                      />
                    </div>
                    {errors.houseNo && touched.houseNo ? (
                      <p style={{ color: "#FF0000", fontSize: "12px" }}>
                        {errors.houseNo}
                      </p>
                    ) : null}
                  </div>

                  <div className="col-md-6">
                    <label htmlFor="area">
                      Area / Building Name{" "}
                      <span className={styles.mandatory}> *</span>
                    </label>
                    <div className={styles.inputbx}>
                      <Field
                        type="text"
                        name="area"
                        id="area"
                        className="form-control"
                        placeholder="Enter Area Name"
                      />
                    </div>
                    {errors.area && touched.area ? (
                      <p style={{ color: "#FF0000", fontSize: "12px" }}>
                        {errors.area}
                      </p>
                    ) : null}
                  </div>

                  <div className="col-md-12">
                    <label htmlFor="landmark">Landmark</label>
                    <div className={styles.inputbx}>
                      <Field
                        type="text"
                        name="landmark"
                        id="landmark"
                        className="form-control"
                        placeholder="Enter Landmark"
                      />
                    </div>
                  </div>

                  <div className="col-md-12">
                    <label htmlFor="city" className="form-label">
                      City <span className={styles.mandatory}> *</span>
                    </label>
                    <div className={styles.searchWrapper}>
                      <CityInput
                        setSelectedCityOption={setSelectedCityOption}
                        setInputValue={setInputValue}
                        selectedCityOption={selectedCityOption}
                        inputValue={inputValue}
                      />
                    </div>
                    {errors.city && touched.city ? (
                      <p style={{ color: "#FF0000", fontSize: "12px" }}>
                        {errors.city}
                      </p>
                    ) : null}
                  </div>

                  <div className="col-12">
                    <label htmlFor="your-comments" className="form-label">
                      Any Comments
                      {/* <span className={styles.mandatory}> *</span> */}
                    </label>
                    <div className={styles.inputbx}>
                      <Field
                        as="textarea"
                        rows="5"
                        className={`${styles.textArea} form-control`}
                        id="message"
                        name="message"
                      />
                    </div>
                    {errors.message && touched.message ? (
                      <p style={{ color: "#FF0000", fontSize: "12px" }}>
                        {errors.message}
                      </p>
                    ) : null}
                  </div>
                  {ConfigData?.recaptcha_site_key && (
                    <div className="col-12">
                      <ReCAPTCHA
                        sitekey={ConfigData?.recaptcha_site_key}
                        onChange={(e) => onChange(setFieldValue, e)}
                      />
                      {errors.captcha && touched.captcha ? (
                        <p style={{ color: "#FF0000", fontSize: "12px" }}>
                          {errors.captcha}
                        </p>
                      ) : null}
                    </div>
                  )}

                  <div className="col-12">
                    <div className={styles.checkWrapper}>
                      <Field type="checkbox" name="terms" />
                      <label htmlFor="terms" className="form-label">
                        I hereby agree and authorize Hettich India Pvt. Ltd and
                        all its subsidiaries to call / email / SMS / WhatsApp me
                        with product / service/ offer /other communications
                      </label>
                    </div>
                    {errors.terms && touched.terms ? (
                      <p style={{ color: "#FF0000", fontSize: "12px" }}>
                        {errors.terms}
                      </p>
                    ) : null}
                  </div>

                  <div className="col-12">
                    {!freeDesignFormLoading &&
                      freeDesignFormError?.message(
                        <p
                          style={{
                            textAlign: "center",
                            color: "#00FF00",
                            fontSize: "12px",
                          }}
                        >
                          {freeDesignFormError?.message}
                        </p>
                      )}

                    <div className="row">
                      <div className="col-md-12 text-center">
                        <button
                          type="submit"
                          className={`btn ${styles["btn-subit"]}`}
                          disabled={freeDesignFormLoading}
                        >
                          {freeDesignFormLoading ? (
                            <span
                              class="spinner-border spinner-border-sm text-light"
                              role="status"
                            >
                              <span class="visually-hidden">Loading...</span>
                            </span>
                          ) : (
                            "SUBMIT"
                          )}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </Form>
            );
          }}
        </Formik>
      </Modal.Body>
    </Modal>
  );

  const installationModal = (
    <Modal
      show={showInstallationModal}
      onHide={() => setShowInstallationModal(false)}
      className={styles.installationModal}
      centered
    >
      <Modal.Header className={styles.modalHeader}>
        <Modal.Title>Installation Services</Modal.Title>
        <div className={styles["close-btn"]}>
          <button
            type="button"
            data-bs-dismiss="modal"
            aria-label="Close"
            onClick={() => {
              resetFunc();
              setShowInstallationModal(false);
              setSelectedCityOption(null);
              setInputValue("");
            }}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="1em"
              height="1em"
              viewBox="0 0 24 24"
            >
              <path
                fill="white"
                d="M18.3 5.71a.996.996 0 0 0-1.41 0L12 10.59L7.11 5.7A.996.996 0 1 0 5.7 7.11L10.59 12L5.7 16.89a.996.996 0 1 0 1.41 1.41L12 13.41l4.89 4.89a.996.996 0 1 0 1.41-1.41L13.41 12l4.89-4.89c.38-.38.38-1.02 0-1.4"
              />
            </svg>
          </button>
        </div>
      </Modal.Header>
      <Modal.Body className={styles.modalBody}>
        <p className={styles.sampleText}>
          Did you recently shop at Hettich product and need help with the
          installation? We got you!
        </p>
        <p className={styles.sampleText}>
          You can call us on our toll-free number at {ConfigData?.support_phone}
          , or write to us at <span>{ConfigData?.support_email}</span>. If you
          prefer a call-back, please fill out the form below, and we will reach
          out to you:
        </p>
        <Formik
          initialValues={{
            fullname: "",
            email: "",
            phone: "",
            sourceOfPurchase: "",
            productPurchased: "",
            type: "Installation Service",
            invoice: "",
            message: "",
            city: "",
            pincode: "",
            terms: false,
            captcha: "",
            houseNo: "",
            area: "",
            landmark: "",
          }}
          validationSchema={installationSchema}
          onSubmit={async (values, { resetForm }) => {
            try {
              const base64String = await fileToBase64(values.invoice);
              if (base64String) {
                const uploadResponse = await uploadContactInvoice({
                  variables: {
                    name: values.invoice.name,
                    savePath: "contact_invoice",
                    base64Value: base64String,
                  },
                });

                if (uploadResponse?.data?.hkUploadFiles?.items[0]) {
                  const { name } =
                    uploadResponse?.data?.hkUploadFiles?.items[0];
                  if (name) {
                    const response = await installationForm({
                      variables: {
                        fullname: values.fullname,
                        email: values.email,
                        message: values.message,
                        type: "Installation Service",
                        phone: values.phone,
                        sourceOfPurchase: values.sourceOfPurchase,
                        productPurchased: values.productPurchased,
                        invoice: name,
                        city: values.city,
                        pincode: values.pincode,
                        g_captcha_response: values.captcha,
                        houseNo: values.houseNo,
                        areaName: values.area,
                        landmark: values.landmark,
                      },
                    });

                    if (response?.data?.contactusFormSubmit?.success_message) {
                      resetForm();
                      setShowInstallationModal(false);
                      addToast(
                        response?.data?.contactusFormSubmit?.success_message,
                        {
                          appearance: "success",
                          autoDismiss: true,
                          style: {
                            backgroundColor:
                              "#027B51" /* Custom background color */,
                            color: "#fff" /* Custom text color */,
                          },
                        }
                      );
                      setSelectedCityOption(null);
                      setInputValue("");
                    }
                  }
                }
              }
            } catch (error) {
              addToast("Something went wrong", {
                appearance: "error",
                autoDismiss: true,
                style: {
                  backgroundColor: "#DB1F00",
                  color: "#fff",
                },
              });
            }
          }}
        >
          {({
            values,
            errors,
            touched,
            resetForm,
            setFieldTouched,
            setFieldValue,
            setFieldError,
          }) => {
            resetFunc = resetForm;
            return (
              <Form>
                <div className="row g-3">
                  <div className="col-md-6">
                    <label htmlFor={"fullname"} className="form-label">
                      Name <span className={styles.mandatory}> *</span>
                    </label>
                    <div className={styles.inputbx}>
                      <Field
                        placeholder="Enter Name"
                        id="fullname"
                        name="fullname"
                        className="form-control"
                      />
                    </div>
                    {errors.fullname && touched.fullname ? (
                      <p style={{ color: "#FF0000", fontSize: "12px" }}>
                        {errors.fullname}
                      </p>
                    ) : null}
                  </div>

                  <div className="col-md-6">
                    <label htmlFor={"email"} className="form-label">
                      Email <span className={styles.mandatory}> *</span>
                    </label>
                    <div className={styles.inputbx}>
                      <Field
                        placeholder="Enter Email"
                        id="email"
                        name="email"
                        className="form-control"
                      />
                    </div>
                    {errors.email && touched.email ? (
                      <p style={{ color: "#FF0000", fontSize: "12px" }}>
                        {errors.email}
                      </p>
                    ) : null}
                  </div>
                  <div className="col-md-6">
                    <label htmlFor={"phone"} className="form-label">
                      Contact Number{" "}
                      <span className={styles.mandatory}> *</span>
                    </label>
                    <div className={styles.inputbx}>
                      <Field
                        type="text"
                        name="phone"
                        id="phone"
                        className="form-control"
                        placeholder="Enter Contact Number"
                      />
                    </div>
                    {errors.phone && touched.phone ? (
                      <p style={{ color: "#FF0000", fontSize: "12px" }}>
                        {errors.phone}
                      </p>
                    ) : null}
                  </div>
                  <div className="col-md-6">
                    <label htmlFor={"productName"} className="form-label">
                      Product Purchased{" "}
                    </label>
                    <div className={styles.inputbx}>
                      <Field
                        type="text"
                        name="productPurchased"
                        id="productPurchased"
                        className="form-control"
                        placeholder="Enter Product Purchased"
                      />
                    </div>
                    {errors.productPurchased && touched.productPurchased ? (
                      <p style={{ color: "#FF0000", fontSize: "12px" }}>
                        {errors.productPurchased}
                      </p>
                    ) : null}
                  </div>
                  <div className="col-md-6">
                    <label htmlFor={"sourceOfPurchase"} className="form-label">
                      Source Of Purchase{" "}
                      <span className={styles.mandatory}> *</span>
                    </label>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "10px",
                      }}
                    >
                      <label
                        className="form-label"
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "6px",
                        }}
                      >
                        <Field
                          type="radio"
                          name="sourceOfPurchase"
                          value="Online"
                          id="Online"
                        />
                        Online
                      </label>
                      <label
                        className="form-label"
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "6px",
                        }}
                      >
                        <Field
                          type="radio"
                          name="sourceOfPurchase"
                          value="Offline"
                          id="Offline"
                        />
                        Offline
                      </label>
                    </div>
                    {errors.sourceOfPurchase && touched.sourceOfPurchase ? (
                      <p style={{ color: "#FF0000", fontSize: "12px" }}>
                        {errors.sourceOfPurchase}
                      </p>
                    ) : null}
                  </div>
                  <div className="col-md-6">
                    <label htmlFor={"invoice"} className="form-label">
                      Invoice <span className={styles.mandatory}> *</span>
                      <span
                        style={{ fontSize: "12px" }}
                      >{` (pdf only upto ${MAX_SIZE_MB}mb)`}</span>
                    </label>
                    <div className={styles.fileWrapper}>
                      <input
                        id="invoice"
                        name="invoice"
                        type="file"
                        accept=".pdf" // Restrict file selection to PDFs
                        onChange={(event) => {
                          const file = event.currentTarget.files[0];
                          if (file) {
                            if (file?.type !== "application/pdf") {
                              setFieldError(
                                "invoice",
                                "File format not supported"
                              );
                            } else {
                              setFieldValue("invoice", file);
                            }
                          }
                          setFieldTouched("invoice", true);
                        }}
                        onBlur={() => {
                          if (values?.invoice) {
                            setFieldError("invoice", null);
                          } else {
                            setFieldError(
                              "invoice",
                              "Please upload the invoice"
                            );
                          }
                          setFieldTouched("invoice", true);
                        }}
                        className={styles.inputFile}
                      />
                      <span className={styles.uploadFile}>
                        <span className={styles.uploadText}>Upload file</span>
                        <span className={styles.fileName}>
                          {values?.invoice
                            ? values?.invoice?.name
                            : "No file chosen"}
                        </span>
                      </span>
                    </div>
                    {errors.invoice && touched.invoice && !values.invoice ? (
                      <p style={{ color: "#FF0000", fontSize: "12px" }}>
                        {errors.invoice}
                      </p>
                    ) : touched.invoice && !values.invoice ? (
                      <p style={{ color: "#FF0000", fontSize: "12px" }}>
                        Please upload the invoice
                      </p>
                    ) : null}
                  </div>

                  <div className="col-md-6">
                    <label htmlFor="houseNo">
                      House / Flat No.{" "}
                      <span className={styles.mandatory}> *</span>
                    </label>
                    <div className={styles.inputbx}>
                      <Field
                        type="text"
                        name="houseNo"
                        id="houseNo"
                        className="form-control"
                        placeholder="Enter House Number"
                      />
                    </div>
                    {errors.houseNo && touched.houseNo ? (
                      <p style={{ color: "#FF0000", fontSize: "12px" }}>
                        {errors.houseNo}
                      </p>
                    ) : null}
                  </div>

                  <div className="col-md-6">
                    <label htmlFor="area">
                      Area / Building Name{" "}
                      <span className={styles.mandatory}> *</span>
                    </label>
                    <div className={styles.inputbx}>
                      <Field
                        type="text"
                        name="area"
                        id="area"
                        className="form-control"
                        placeholder="Enter Area Name"
                      />
                    </div>
                    {errors.area && touched.area ? (
                      <p style={{ color: "#FF0000", fontSize: "12px" }}>
                        {errors.area}
                      </p>
                    ) : null}
                  </div>

                  <div className="col-md-12">
                    <label htmlFor="landmark">Landmark</label>
                    <div className={styles.inputbx}>
                      <Field
                        type="text"
                        name="landmark"
                        id="landmark"
                        className="form-control"
                        placeholder="Enter Landmark"
                      />
                    </div>
                  </div>

                  <div className="col-md-12">
                    <label htmlFor="city" className="form-label">
                      City <span className={styles.mandatory}> *</span>
                    </label>
                    <div className={styles.searchWrapper}>
                      <CityInput
                        setSelectedCityOption={setSelectedCityOption}
                        setInputValue={setInputValue}
                        selectedCityOption={selectedCityOption}
                        inputValue={inputValue}
                      />
                    </div>
                    {errors.city && touched.city ? (
                      <p style={{ color: "#FF0000", fontSize: "12px" }}>
                        {errors.city}
                      </p>
                    ) : null}
                  </div>

                  <div className="col-12">
                    <label htmlFor="your-comments" className="form-label">
                      Any Comments
                      {/* <span className={styles.mandatory}> *</span> */}
                    </label>
                    <Field
                      as="textarea"
                      rows="5"
                      className={`${styles.textArea} form-control`}
                      id="message"
                      name="message"
                    />
                    {errors.message && touched.message ? (
                      <p style={{ color: "#FF0000", fontSize: "12px" }}>
                        {errors.message}
                      </p>
                    ) : null}
                  </div>

                  {ConfigData?.recaptcha_site_key && (
                    <div className="col-12">
                      <ReCAPTCHA
                        sitekey={ConfigData?.recaptcha_site_key}
                        onChange={(e) => onChange(setFieldValue, e)}
                      />
                      {errors.captcha && touched.captcha ? (
                        <p style={{ color: "#FF0000", fontSize: "12px" }}>
                          {errors.captcha}
                        </p>
                      ) : null}
                    </div>
                  )}

                  <div className="col-12">
                    <div className={styles.checkWrapper}>
                      <Field type="checkbox" name="terms" />
                      <label htmlFor="terms" className="form-label">
                        I hereby agree and authorize Hettich India Pvt. Ltd and
                        all its subsidiaries to call / email / SMS / WhatsApp me
                        with product / service/ offer /other communications
                      </label>
                    </div>
                    {errors.terms && touched.terms ? (
                      <p style={{ color: "#FF0000", fontSize: "12px" }}>
                        {errors.terms}
                      </p>
                    ) : null}
                  </div>

                  {uploadInvoiceError?.message && (
                    <p style={{ color: "#FF0000", fontSize: "12px" }}>
                      {uploadInvoiceError?.message}
                    </p>
                  )}

                  {installationFormError?.message && (
                    <p style={{ color: "#FF0000", fontSize: "12px" }}>
                      {installationFormError?.message}
                    </p>
                  )}
                  <div className="col-12">
                    <div className="row">
                      <div className="col-md-12 text-center">
                        <button
                          type="submit"
                          className={`btn ${styles["btn-subit"]}`}
                          disabled={installationFormLoading}
                        >
                          {installationFormLoading ? (
                            <span
                              class="spinner-border spinner-border-sm text-light"
                              role="status"
                            >
                              <span class="visually-hidden">Loading...</span>
                            </span>
                          ) : (
                            "SUBMIT"
                          )}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </Form>
            );
          }}
        </Formik>
      </Modal.Body>
    </Modal>
  );

  return {
    data,
    loading,
    discoveryError,
    installationModal,
    setShowInstallationModal,
    setShowTechnicalAssistanceModal,
    setShowFreeDesignModal,
    freeDesignModal,
    technicalAssistanceModal,
  };
};
